import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios'
import { getStoredToken, clearStoredToken } from '@/lib/auth/token-storage'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.pmgsc-data.org'

// Custom API error class with user-friendly messages
export class ApiError extends Error {
  status: number
  code: string
  isNetworkError: boolean
  isCorsError: boolean

  constructor(message: string, status: number, code: string, isNetworkError = false, isCorsError = false) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.isNetworkError = isNetworkError
    this.isCorsError = isCorsError
  }
}

// Extract user-friendly error message from axios error
function extractErrorMessage(error: AxiosError): { message: string; code: string; isCors: boolean } {
  // Network error (no response) - often CORS
  if (!error.response) {
    // Check if it's likely a CORS error
    const isCors = error.message === 'Network Error' ||
                   error.code === 'ERR_NETWORK' ||
                   error.message.toLowerCase().includes('cors')

    if (isCors) {
      return {
        message: 'Unable to connect to the API server. This may be a CORS configuration issue.',
        code: 'CORS_ERROR',
        isCors: true,
      }
    }

    return {
      message: 'Network error. Please check your connection and try again.',
      code: 'NETWORK_ERROR',
      isCors: false,
    }
  }

  // Try to extract message from response
  const data = error.response.data as Record<string, unknown> | undefined

  if (data) {
    // Common API error response formats
    if (typeof data.message === 'string') return { message: data.message, code: String(data.code || error.response.status), isCors: false }
    if (typeof data.error === 'string') return { message: data.error, code: String(data.code || error.response.status), isCors: false }
    if (typeof data.detail === 'string') return { message: data.detail, code: String(data.code || error.response.status), isCors: false }
  }

  // Default messages by status code
  const statusMessages: Record<number, string> = {
    400: 'Invalid request. Please check your input.',
    401: 'Authentication required. Please log in.',
    403: 'You do not have permission to perform this action.',
    404: 'The requested resource was not found.',
    409: 'A conflict occurred. The resource may already exist.',
    422: 'Validation failed. Please check your input.',
    429: 'Too many requests. Please wait and try again.',
    500: 'Server error. Please try again later.',
    502: 'Server is temporarily unavailable. Please try again later.',
    503: 'Service unavailable. Please try again later.',
  }

  return {
    message: statusMessages[error.response.status] || `Request failed (${error.response.status})`,
    code: String(error.response.status),
    isCors: false,
  }
}

// Create axios instance
export const httpClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Auth interceptor - adds Bearer token
httpClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getStoredToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor - handles errors
httpClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      clearStoredToken()
      window.location.href = '/login'
    }

    // Log errors in development
    if (import.meta.env.DEV) {
      console.error('[API Error]', {
        url: error.config?.url,
        status: error.response?.status,
        message: error.message,
        data: error.response?.data,
      })
    }

    // Convert to ApiError with user-friendly message
    const { message, code, isCors } = extractErrorMessage(error)
    const isNetworkError = !error.response
    const status = error.response?.status || 0

    return Promise.reject(new ApiError(message, status, code, isNetworkError, isCors))
  }
)

// Typed API client wrapper
export const apiClient = {
  get: <T>(url: string, params?: Record<string, unknown>) =>
    httpClient.get<T>(url, { params }).then((res) => res.data),

  post: <T>(url: string, data?: unknown) =>
    httpClient.post<T>(url, data).then((res) => res.data),

  patch: <T>(url: string, data?: unknown) =>
    httpClient.patch<T>(url, data).then((res) => res.data),

  delete: <T>(url: string) =>
    httpClient.delete<T>(url).then((res) => res.data),

  // For file uploads
  upload: <T>(url: string, formData: FormData) =>
    httpClient.post<T>(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((res) => res.data),
}
